import { SCHEMATIC_CATEGORY_ROUTES, SCHEMATIC_EXAMPLE_ROUTES } from "./schematic-library-routes";

/**
 * The public resource routes and the index pages that list them. Keep this
 * module dependency-free — both the browser entry point and the Cloudflare
 * middleware import it before any page data or UI should load.
 */
export const RESOURCE_PAGE_ROUTES = [
  ...SCHEMATIC_CATEGORY_ROUTES,
  ...SCHEMATIC_EXAMPLE_ROUTES,
  { id: "wire-size", kind: "tool", path: "/tools/wire-size/" },
  { id: "voltage-drop", kind: "tool", path: "/tools/voltage-drop/" },
  { id: "dc-dc-charger-size", kind: "tool", path: "/tools/dc-dc-charger-size/" },
  { id: "boat", kind: "diagram", path: "/wiring-diagrams/boat/" },
  { id: "single-battery-boat", kind: "diagram", path: "/wiring-diagrams/single-battery-boat/" },
  { id: "campervan", kind: "diagram", path: "/wiring-diagrams/campervan/" },
  { id: "dual-battery", kind: "diagram", path: "/wiring-diagrams/dual-battery/" },
  {
    id: "lead-acid-to-lifepo4",
    kind: "guide",
    path: "/guides/lead-acid-to-lifepo4/",
  },
  {
    id: "what-crimp-checks",
    kind: "guide",
    path: "/guides/what-crimp-checks/",
  },
  {
    id: "battery-runtime",
    kind: "guide",
    path: "/guides/battery-runtime/",
  },
  {
    id: "12v-vs-24v",
    kind: "guide",
    path: "/guides/12v-vs-24v/",
  },
  {
    id: "campervan-electrical-planning",
    kind: "guide",
    path: "/guides/campervan-electrical-planning/",
  },
  {
    id: "lifepo4-fuse-size",
    kind: "tool",
    path: "/tools/lifepo4-fuse-size/",
  },
  {
    id: "voltplan-alternative",
    kind: "comparison",
    path: "/alternatives/voltplan/",
  },
  {
    id: "12v-sim-alternative",
    kind: "comparison",
    path: "/alternatives/12v-sim/",
  },
  {
    id: "wireframe-alternative",
    kind: "comparison",
    path: "/alternatives/wireframe/",
  },
  {
    id: "marine-power-designer-alternative",
    kind: "comparison",
    path: "/alternatives/marine-power-designer/",
  },
] as const;

/**
 * One index per namespace. They exist even while a namespace holds a single
 * page: every leaf breadcrumb needs a real hub, and a namespace whose root
 * 404s while its sibling's resolves reads as a broken site, not as a choice.
 */
export const RESOURCE_INDEX_ROUTES = [
  { id: "examples", kind: "example", path: "/examples/" },
  { id: "wiring-diagrams", kind: "diagram", path: "/wiring-diagrams/" },
  { id: "guides", kind: "guide", path: "/guides/" },
  { id: "tools", kind: "tool", path: "/tools/" },
  { id: "alternatives", kind: "comparison", path: "/alternatives/" },
] as const;

export type ResourcePageRoute = (typeof RESOURCE_PAGE_ROUTES)[number];
export type ResourcePageId = ResourcePageRoute["id"];
export type ResourcePageKind = ResourcePageRoute["kind"];

export type ResourceIndexRoute = (typeof RESOURCE_INDEX_ROUTES)[number];
export type ResourceIndexId = ResourceIndexRoute["id"];

/** What the entry points dispatch on: which document, and where it belongs. */
export type ResourceRouteMatch =
  | { type: "page"; id: ResourcePageId; path: ResourcePageRoute["path"] }
  | { type: "index"; id: ResourceIndexId; path: ResourceIndexRoute["path"] };

export function resourcePagePath(id: ResourcePageId): ResourcePageRoute["path"] {
  const route = RESOURCE_PAGE_ROUTES.find((candidate) => candidate.id === id);
  // The id is a closed union, so this is an exhaustiveness guard for a future
  // edit that changes the type without adding the corresponding route.
  if (!route) throw new Error(`Missing resource route: ${id}`);
  return route.path;
}

export function resourceIndexPath(
  id: ResourceIndexId,
): ResourceIndexRoute["path"] {
  const route = RESOURCE_INDEX_ROUTES.find((candidate) => candidate.id === id);
  if (!route) throw new Error(`Missing resource index route: ${id}`);
  return route.path;
}

/** The index a leaf page is filed under, for its breadcrumb and its "back" link. */
export function resourceIndexForKind(kind: ResourcePageKind): ResourceIndexRoute {
  const route = RESOURCE_INDEX_ROUTES.find(
    (candidate) => candidate.kind === kind,
  );
  if (!route) throw new Error(`Missing resource index for kind: ${kind}`);
  return route;
}

/** Match only a published resource URL, with or without its trailing slash. */
export function matchResourceRoute(
  pathname: string,
): ResourceRouteMatch | null {
  const canonicalPath = pathname.endsWith("/") ? pathname : `${pathname}/`;
  const page = RESOURCE_PAGE_ROUTES.find(
    (candidate) => candidate.path === canonicalPath,
  );
  if (page) return { type: "page", id: page.id, path: page.path };
  const index = RESOURCE_INDEX_ROUTES.find(
    (candidate) => candidate.path === canonicalPath,
  );
  if (index) return { type: "index", id: index.id, path: index.path };
  return null;
}
